import { useQuery } from "@tanstack/react-query";

import { fetchDashboardSummary, fetchSystemHealth } from "@/api/endpoints/dashboard";

function StatTile({ label, value, tone = "default" }: { label: string; value: string; tone?: "default" | "warning" | "critical" }) {
  const toneClass = tone === "critical" ? "text-critical" : tone === "warning" ? "text-warning" : "text-text";
  return (
    <div className="rounded-lg border border-border bg-surface p-5">
      <p className="mb-2 font-mono text-xs uppercase tracking-wide text-text-muted">{label}</p>
      <p className={`text-3xl font-semibold tabular-nums ${toneClass}`}>{value}</p>
    </div>
  );
}

function usageTone(percent: number): "default" | "warning" | "critical" {
  if (percent >= 90) return "critical";
  if (percent >= 75) return "warning";
  return "default";
}

export function DashboardPage() {
  const summaryQuery = useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: fetchDashboardSummary,
    refetchInterval: 15_000,
  });
  const healthQuery = useQuery({
    queryKey: ["dashboard", "system-health"],
    queryFn: fetchSystemHealth,
    refetchInterval: 15_000,
  });

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h2 className="mb-3 font-mono text-xs uppercase tracking-wide text-text-muted">
          RADIUS activity
        </h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatTile label="Online users" value={summaryQuery.data ? String(summaryQuery.data.online_users) : "—"} />
          <StatTile label="Active sessions" value={summaryQuery.data ? String(summaryQuery.data.active_sessions) : "—"} />
          <StatTile label="Logins today" value={summaryQuery.data ? String(summaryQuery.data.logins_today) : "—"} />
          <StatTile
            label="Failed logins today"
            value={summaryQuery.data ? String(summaryQuery.data.failed_logins_today) : "—"}
            tone={summaryQuery.data && summaryQuery.data.failed_logins_today > 0 ? "warning" : "default"}
          />
        </div>
        {summaryQuery.isError && (
          <p className="mt-2 text-sm text-critical">Couldn't load RADIUS activity — is the backend reachable?</p>
        )}
      </section>

      <section>
        <h2 className="mb-3 font-mono text-xs uppercase tracking-wide text-text-muted">
          Host — 172.17.16.171
        </h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <StatTile
            label="CPU"
            value={healthQuery.data ? `${healthQuery.data.cpu_percent}%` : "—"}
            tone={healthQuery.data ? usageTone(healthQuery.data.cpu_percent) : "default"}
          />
          <StatTile
            label="Memory"
            value={healthQuery.data ? `${healthQuery.data.memory_percent}%` : "—"}
            tone={healthQuery.data ? usageTone(healthQuery.data.memory_percent) : "default"}
          />
          <StatTile
            label="Disk"
            value={healthQuery.data ? `${healthQuery.data.disk_percent}%` : "—"}
            tone={healthQuery.data ? usageTone(healthQuery.data.disk_percent) : "default"}
          />
        </div>
        {healthQuery.isError && (
          <p className="mt-2 text-sm text-critical">Couldn't load host metrics — is the backend reachable?</p>
        )}
      </section>

      <p className="text-xs text-text-muted">
        Auth graph, NAS status, and live WebSocket updates land in Stage 2.1 — this view polls
        every 15s for now.
      </p>
    </div>
  );
}
